import React, { FunctionComponent, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Grid, Typography, Button, Option, Colors } from '@inplayer-org/inplayer-ui';
import { Formik, FormikProps } from 'formik';
import styled from 'styled-components';
import { object, number, string, date } from 'yup';
import moment, { Moment } from 'moment';
import omit from 'lodash/omit';

// actions
import { loadCodes, saveCode, updateCode, updateAllCodes } from 'ducks/merchantDucks/items/actions';
import { openModal } from 'ducks/merchantDucks/modal/actions';

// components
import { CenterContent, FullHeightForm, ContentContainer } from 'components/SharedStyledElements';
import AccordionFooter from 'components/AccordionFooter';
import CodesTable from 'pages/MerchantPages/Assets/Codes/components/CodesTable';
import AssetCodeOptions, { CodeTabs, CodeTypes } from 'pages/MerchantPages/Assets/Codes/containers/AssetCodeOptions';
import ConfirmModal from 'components/Modals/ConfirmModal';
import Loader from 'components/Loader';
import Filter from 'components/Filter';
import Restricted from 'restrictions/roles/components/Restricted';
import SearchTooltip from 'components/SearchTooltip';
import FormAccordionHelper, { CodeOnlySectionType } from 'pages/MerchantPages/Assets/components/AccordionHelper';
import { PlusIcon } from 'pages/MerchantPages/Assets/containers/Assets';
import AddInBulkForm from 'pages/MerchantPages/Assets/Codes/components/AddInBulkForm';
import AssetCodesReports from 'pages/MerchantPages/Assets/Codes/containers/AssetCodesReports';
import RestrictedByRule from 'restrictions/roles/components/RestrictedByRule';

// hooks
import { useBoolean } from 'hooks/useBoolean';
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// types
import { ModalNames } from 'ducks/merchantDucks/modal/types';
import { Codes, CodeType, BulkCodeType } from 'ducks/merchantDucks/items/types';
import { Optional } from 'utils/types';

// selectors
import { getItemCodes } from 'ducks/merchantDucks/items/selectors';

// constants
import Rules from 'restrictions/roles/constants/rules';

const { Cell, Container } = Grid;

export const CodesDetails = styled(Cell)`
  padding: 2rem 6%;
`;

const AddButton = styled(Button)`
  margin-right: 1rem;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  margin-bottom: 2rem;
`;

const HeadingContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Title = styled(Typography)`
  color: ${Colors.fontGray};
  text-transform: uppercase;
  font-weight: 700;
`;

interface FormValues {
  id: number;
  code: string;
  startDate: Optional<Moment>;
  endDate: Optional<Moment>;
  type: string;
  // empty string value for bulk action
  concurrentSessions: number | string;
}

interface OwnProps {
  closePanel: () => void;
  accordionLabel: string;
}

type Props = OwnProps;

export enum ButtonTypes {
  ADD = 'save',
  UPDATE = 'update',
  BULK = 'bulk',
}

interface SearchParams {
  code: string;
}

const bulkValidationSchema = object().shape({
  concurrentSessions: number()
    .min(0, 'Cuncurrent sessions have to be at least 0 or positive number')
    .typeError('The concurrent sessions must be a number.'),
});

const options: Array<Option> = [{ value: 'code', displayName: 'Passcode' }];

const CodesContainer: FunctionComponent<Props> = ({ closePanel, accordionLabel }) => {
  const initialCodeDetails: CodeType = {
    id: 0,
    code: '',
    endDate: 0,
    startDate: 0,
    type: '',
    concurrentSessions: '',
  };

  const initialBulkCodeDetails: BulkCodeType = {
    itemId: 0,
    startDate: null,
    endDate: null,
    type: '',
    concurrentSessions: '',
  };

  const dispatch = useDispatch();
  const { id: itemId } = useParams<{ id: string }>();
  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);

  // state
  const isAddCodeSelected = useBoolean(false);
  const [codeToRemove, setCodeToRemove] = useState('');
  const isCodeSelected = useBoolean(false);
  const [buttonType, setButtonType] = useState('');
  const [codeDetails, setCodeDetails] = useState(initialCodeDetails);
  const [page, setPage] = useState(1);
  const [tags, setTags] = useState<SearchParams>({ code: '' });
  const [bulkData, setBulkData] = useState(initialBulkCodeDetails);
  const [activeTab, setActiveTab] = useState(0);
  const isAddInBulk = useBoolean(false);
  const isReportsClicked = useBoolean(false);

  const codes: Codes = useSelector(getItemCodes);

  useEffect(() => {
    // BE pagination starts from 0
    dispatch(loadCodes({ itemId: Number(itemId), page: 0 }));
  }, [loadCodes]);

  useEffect(() => {
    if (isCodeSelected.value) {
      isAddCodeSelected.setTrue();
    } else {
      isAddCodeSelected.setFalse();
    }
  }, [isCodeSelected.value]);

  const deleteAssetCode = () => {
    // TODO: Implement delete code functionality when BE is ready
  };

  const handleShowModal = (code: string) => {
    setCodeToRemove(code);
    dispatch(openModal(ModalNames.DeleteAssetCode));
  };

  const selectCode = (selectedCode: CodeType) => {
    setCodeDetails(selectedCode);
    isCodeSelected.setTrue();
    setButtonType(ButtonTypes.UPDATE);
  };

  const handleCancel = (resetForm: () => void) => () => {
    resetForm();
    closePanel();
  };

  const handleBackToCodesTable = (resetForm?: () => void) => {
    isAddInBulk.setFalse();
    isCodeSelected.setFalse();
    isAddCodeSelected.setFalse();
    isReportsClicked.setFalse();
    setActiveTab(0);
    // Because resetForm is not defined in case of bulk action,
    // code details should be set to initial values
    setCodeDetails(initialCodeDetails);
    if (resetForm && typeof resetForm === 'function') {
      resetForm();
    }
  };

  const handlePageClick = (pageNumber: number) => {
    setPage(pageNumber);

    // Send pageNumber reduced by 1 because BE pagination starts from 0
    dispatch(loadCodes({ itemId: Number(itemId), page: pageNumber - 1, code: tags.code }));
  };

  const handleAddTag = (searchParams: SearchParams) => {
    setTags(searchParams);
    // First page in pagination is 1
    setPage(1);
    // BE pagination starts from 0
    dispatch(loadCodes({ itemId: Number(itemId), page: 0, code: searchParams.code }));
  };

  const handleOpenUpdateCodeForm = () => {
    setButtonType(ButtonTypes.BULK);
    isAddCodeSelected.setTrue();
  };

  const handleUpdateAllCodes = async () => {
    const hasError = await dispatch(updateAllCodes(bulkData));

    if (!hasError) {
      handleBackToCodesTable();
    }
  };

  const handleOpenAddCodeForm = () => {
    setButtonType(ButtonTypes.ADD);
    isAddCodeSelected.setTrue();
  };

  const handleOpenBulkCodeForm = () => {
    isAddInBulk.setTrue();
  };

  const handleOpenReports = () => {
    isReportsClicked.setTrue();
  };

  const handleSetActiveTab = (tabIndex: number) => setActiveTab(tabIndex);

  const onSubmitForm = async (data: FormValues) => {
    if (buttonType === ButtonTypes.BULK) {
      setBulkData({
        // Code is unnecessery for bulk action
        ...omit(data, ['code']),
        itemId: Number(itemId),
      });
      dispatch(openModal(ModalNames.UpdateAllAssetCodes));
      return;
    }

    const codeAction =
      buttonType === ButtonTypes.ADD ? saveCode({ ...data, itemId: Number(itemId) }) : updateCode(data);
    const hasError = await dispatch(codeAction);
    if (!hasError) {
      handleBackToCodesTable();
    }
  };

  const { id, code, startDate, endDate, type, concurrentSessions } = codeDetails || {
    id: 0,
    code: '',
    startDate: null,
    endDate: null,
    type: '',
    concurrentSessions: '',
  };

  const initialValues: FormValues = {
    id,
    code,
    startDate: startDate ? moment(startDate * 1000).utc() : null,
    endDate: endDate ? moment(endDate * 1000).utc() : null,
    type,
    concurrentSessions,
  };

  const saveValidationSchema = object().shape({
    code: string()
      .required('*Required')
      .matches(/^[a-zA-Z0-9]+$/, 'Special characters are not allowed as content for a passcode')
      .min(6, 'Passcode must be at least 6 characters')
      .max(512, 'Passcode must be at most 512 characters'),
    startDate: date().required('*Required').nullable(),
    endDate: date().required('*Required').nullable(),
    concurrentSessions: number().when(['type'], {
      is: (codeType: string) =>
        codeType === CodeTypes.PRIVATE && (buttonType === ButtonTypes.ADD || buttonType === ButtonTypes.UPDATE),
      then: number()
        .required('*Required.')
        .min(0, 'Concurrent sessions have to be at least 0 or positive number')
        .typeError('The concurrent sessions must be a number.'),
    }),
    type: string().required('*Required'),
  });

  const renderHeaderSection = () => {
    const showUpdateAllButton = codes.collection.length !== 0;

    return (
      <>
        <HeadingContainer>
          <Title variant="p">Passcodes</Title>
          <ButtonsContainer>
            {showUpdateAllButton && (
              <Restricted isRestricted={isReadOnly}>
                <Button onClick={handleOpenUpdateCodeForm}>Update all</Button>
              </Restricted>
            )}
          </ButtonsContainer>
        </HeadingContainer>
        <Container>
          <Cell width={12}>
            <SearchTooltip />
          </Cell>
          <Cell width={12}>
            <Filter options={options} onSearch={handleAddTag} externalFilters={tags} />
          </Cell>
        </Container>
      </>
    );
  };

  const renderCodesTable = () => {
    return (
      <Cell width={12}>
        <Container columns={12} rowGap="1rem">
          <Cell width={12}>
            <CodesTable
              codes={codes}
              handleShowModal={handleShowModal}
              selectCode={selectCode}
              handlePageClick={handlePageClick}
              paginationPage={page}
              renderHeaderSection={renderHeaderSection}
            />
          </Cell>
        </Container>
      </Cell>
    );
  };

  const renderCodes = () => {
    return (
      <>
        <CodesDetails>
          <ButtonsContainer>
            <Restricted isRestricted={isReadOnly}>
              <div>
                <AddButton onClick={handleOpenAddCodeForm}>
                  <PlusIcon>+</PlusIcon>
                  Add passcode
                </AddButton>
                <AddButton onClick={handleOpenBulkCodeForm}>Bulk add</AddButton>
              </div>
            </Restricted>
            <RestrictedByRule rule={Rules.DISABLE_ALL_REPORTS_SECTIONS}>
              <Button buttonModifiers={['buttonLink']} onClick={handleOpenReports}>
                View reports
              </Button>
            </RestrictedByRule>
          </ButtonsContainer>
          <Container columns={12} rowGap="1.5rem">
            {renderCodesTable()}
          </Container>
        </CodesDetails>
        <ConfirmModal
          modalName={ModalNames.DeleteAssetCode}
          handleConfirm={deleteAssetCode}
          message="Are you sure you want to delete the passcode?"
        />
      </>
    );
  };

  const renderEditCodeForm = () => {
    if (codes.isUpdating || codes.isCreating) {
      return <Loader position="absolute" />;
    }

    const validationSchema =
      buttonType === ButtonTypes.ADD || buttonType === ButtonTypes.UPDATE ? saveValidationSchema : bulkValidationSchema;
    return (
      <>
        <Formik<FormValues>
          enableReinitialize
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={onSubmitForm}
        >
          {({ resetForm, isValid, isSubmitting, dirty, values }: FormikProps<FormValues>) => {
            const disableCodeSaveButton = isReadOnly || !isValid || isSubmitting || !dirty;
            const showAccordionFooter = activeTab === CodeTabs.CodeDetails;

            return (
              <FullHeightForm>
                <FormAccordionHelper
                  initialValues={initialValues}
                  closePanel={closePanel}
                  index={CodeOnlySectionType.CodeOptions}
                />
                <ContentContainer>
                  <CenterContent>
                    <AssetCodeOptions
                      codeDetails={codeDetails}
                      buttonType={buttonType}
                      activeTab={activeTab}
                      handleBackToCodesTable={() => handleBackToCodesTable(resetForm)}
                      setActiveTab={handleSetActiveTab}
                      // Reset only concurrentSessions field on adding new code
                      // when code type is set to public
                      resetForm={() => {
                        resetForm({ values: { ...values, concurrentSessions: '' } });
                      }}
                    />
                  </CenterContent>
                  {showAccordionFooter && (
                    <AccordionFooter
                      accordionLabel={accordionLabel}
                      handleCancel={handleCancel(resetForm)}
                      disabled={disableCodeSaveButton}
                    />
                  )}
                </ContentContainer>
              </FullHeightForm>
            );
          }}
        </Formik>
        <ConfirmModal
          modalName={ModalNames.UpdateAllAssetCodes}
          message="Are you sure you want to proceed?"
          description="The changes you specified will be applied to all passcodes added to the asset you are managing."
          handleConfirm={handleUpdateAllCodes}
          buttonText="Save"
        />
      </>
    );
  };

  const renderAddInBulkForm = () => {
    return (
      <ContentContainer>
        <CenterContent>
          <AddInBulkForm handleBackToCodesTable={handleBackToCodesTable} />
        </CenterContent>
      </ContentContainer>
    );
  };

  if (isReportsClicked.value) {
    return (
      <ContentContainer>
        <CenterContent>
          <AssetCodesReports handleBackToCodesTable={handleBackToCodesTable} />
        </CenterContent>
      </ContentContainer>
    );
  }

  if (isAddInBulk.value) {
    return renderAddInBulkForm();
  }

  if (isAddCodeSelected.value) {
    return renderEditCodeForm();
  }

  return renderCodes();
};

export default CodesContainer;
